import { Action, DailyStats, Hotel } from './types';
import { addDays, format } from 'date-fns';
const addDaysFn = addDays; // Alias for internal use if needed or just use addDays directly. Actually I used addDaysFn in my content.

export const mockHotel: Hotel = {
    id: 'h_123',
    name: 'SynthHotel Berlin Mitte',
    timezone: 'Europe/Berlin',
    currency: 'EUR',
};

const today = new Date();

export const mockActions: Action[] = [
    {
        id: 'a_1',
        title: 'Increase Rate for High Demand Weekend',
        titleKey: 'action.dynamic.weekend.title',
        description: 'Occupancy crossed 75% for Saturday, pickup is strong (+12 rooms in 24h).',
        descriptionKey: 'action.dynamic.weekend.desc',
        date: format(addDays(today, 5), 'yyyy-MM-dd'),
        roomTypeId: 'rt_double',
        ratePlanId: 'rp_bar',
        currentValue: 140,
        proposedValue: 155,
        reason: ['Occupancy > 75%', 'Pickup Momentum High'],
        impact: '+€350 est. revenue',
        risk: 'low',
        mode: 'manual',
        status: 'pending',
        generatedAt: new Date().toISOString(),
    },
    {
        id: 'a_2',
        title: 'Enable 2-Night Min Stay',
        titleKey: 'action.dynamic.minstay.title',
        description: 'Taylor Swift concert announced. Prevent 1-night stays blocking inventory.',
        descriptionKey: 'action.dynamic.minstay.desc',
        date: format(addDays(today, 45), 'yyyy-MM-dd'),
        roomTypeId: 'all',
        currentValue: '1 night',
        proposedValue: '2 nights',
        reason: ['Event Detected', 'Compression Date'],
        impact: 'Protect ADR',
        risk: 'medium',
        mode: 'assisted',
        status: 'pending',
        generatedAt: new Date().toISOString(),
    },
    {
        id: 'a_3',
        title: 'Lower Rate (Distress)',
        titleKey: 'action.dynamic.distress.title',
        description: 'Occupancy is 35% for next Tuesday. Competitors dropped by €10.',
        descriptionKey: 'action.dynamic.distress.desc',
        date: format(addDays(today, 3), 'yyyy-MM-dd'),
        roomTypeId: 'rt_standard',
        ratePlanId: 'rp_bar',
        currentValue: 110,
        proposedValue: 99,
        reason: ['Low Occ (35%)', 'Competitor Parity'],
        impact: 'Capture volume',
        risk: 'low',
        mode: 'auto',
        status: 'executed', // Already done by auto
        generatedAt: format(addDays(today, -1), 'yyyy-MM-dd'),
    },
    {
        id: 'a_4',
        title: 'Gap Fill: Remove CTA',
        titleKey: 'action.dynamic.gap.title',
        description: 'Single night gap found. Remove Close To Arrival to sell the room.',
        descriptionKey: 'action.dynamic.gap.desc',
        date: format(addDays(today, 1), 'yyyy-MM-dd'),
        roomTypeId: 'rt_suite',
        currentValue: 'CTA Active',
        proposedValue: 'Open',
        reason: ['Fragmented Inventory'],
        impact: '+€280',
        risk: 'low',
        mode: 'assisted',
        status: 'pending',
        generatedAt: new Date().toISOString(),
    }
];

export const mockStats: DailyStats = {
    date: format(today, 'yyyy-MM-dd'),
    occupancy: 68,
    adr: 142,
    revpar: 96.5,
    revenue: 4500,
};

import { startOfMonth, endOfMonth, eachDayOfInterval, isWeekend } from 'date-fns';
import { CalendarDay } from './types';

// Updated to accept a baseDate for navigation
export const getMockCalendarData = (baseDate: Date = new Date()): CalendarDay[] => {
    const start = startOfMonth(baseDate);
    const end = endOfMonth(baseDate);

    const days = eachDayOfInterval({ start, end });

    return days.map((day, index) => {
        // Deterministic 'random' based on date
        const seed = day.getDate() + day.getMonth() * 31 + day.getFullYear() * 365;
        const pseudoRandom = (modifier: number) => {
            const x = Math.sin(seed + modifier) * 10000;
            return x - Math.floor(x);
        };
        // ... rest of logic uses day/pseudoRandom (unchanged logic, just context)
        const isWknd = isWeekend(day);
        const hasEvent = pseudoRandom(1) > 0.85;

        // Use pseudoRandom instead of Math.random
        let occupancy = isWknd ? 75 + pseudoRandom(2) * 20 : 40 + pseudoRandom(3) * 30;
        if (hasEvent) occupancy += 15;
        occupancy = Math.min(100, Math.floor(occupancy));

        const pickup = Math.floor(pseudoRandom(4) * 8) - 2; // -2 to +6

        return {
            date: format(day, 'yyyy-MM-dd'),
            occupancy,
            pickup,
            currentRate: isWknd ? 140 : 110,
            recommendedRate: occupancy > 80 ? (isWknd ? 160 : 130) : undefined,
            event: hasEvent ? 'Concert / Fair' : undefined,
            isHighDemand: occupancy > 80
        };
    });
};

import { InventoryRow, RateSnapshot } from './types';

export const getMockRatesData = (): InventoryRow[] => {
    const today = new Date();
    const dates = Array.from({ length: 14 }, (_, i) => addDaysFn(today, i));

    const roomTypes = [
        { id: 'rt_s', name: 'Standard Room', base: 100 },
        { id: 'rt_d', name: 'Double Superior', base: 140 },
        { id: 'rt_j', name: 'Junior Suite', base: 220 },
    ];

    return roomTypes.map(rt => {
        const data: RateSnapshot[] = dates.map((date, i) => {
            const isWknd = isWeekend(date);
            const basePrice = rt.base + (isWknd ? 30 : 0);
            const price = basePrice + Math.floor(Math.sin(i) * 10); // deterministic wiggle

            // 20% Chance of a proposal
            const hasProposal = (i * basePrice) % 5 === 0;

            return {
                date: format(date, 'yyyy-MM-dd'),
                price,
                minStay: isWknd ? 2 : 1,
                proposedPrice: hasProposal ? price + 15 : undefined,
                proposedMinStay: (isWknd && i % 3 === 0) ? 3 : undefined,
            };
        });

        return {
            roomTypeId: rt.id,
            roomTypeName: rt.name,
            ratePlanName: 'Best Available Rate',
            data
        };
    });
};

import { AutomationSettings } from './types';

export const mockSettings: AutomationSettings = {
    mode: 'assisted',
    maxDailyIncrease: 15, // 15%
    maxDailyDecrease: 8,  // 8%
    minRate: 85,
    maxRate: 450,
    enableWeekendSurge: true,
    autoApproveLowRisk: true,
};

import { AuditLogEntry } from './types';

export const getMockAuditLogs = (): AuditLogEntry[] => {
    return Array.from({ length: 25 }, (_, i) => {
        const date = addDaysFn(new Date(), -1 * Math.floor(i / 3)); // few logs per day
        date.setHours(14 - (i % 8), 30, 0); // vary time

        const isAuto = i % 4 !== 0; // mostly auto
        // Fix: Mod 10 first
        const category = i % 10 === 0 ? 'Sync' : (i % 5 === 0 ? 'Restriction' : 'Rate Change');

        return {
            id: `log_${i}`,
            timestamp: format(date, 'yyyy-MM-dd HH:mm:ss'),
            actor: isAuto ? 'System (AI)' : 'User',
            action: category === 'Restriction' ? 'Applied "Min Stay 2"' : (category === 'Sync' ? 'Sync with Cloudbeds' : 'Update BAR Rate'),
            details: category === 'Rate Change' ? `Changed from €${100 + i} to €${110 + i}` : 'Rule match: Concert Weekend',
            category,
            status: i === 7 ? 'Failed' : 'Success',
            impact: category === 'Rate Change' ? '+€45 Exp. Rev' : undefined,
        };
    });
};
